/**
 * Spatial Intelligence API Client
 * ────────────────────────────────
 * Typed wrapper for /api/spatial/* endpoints.
 * Additive — does not replace any existing API client.
 */
import { apiClient } from './api';

export interface ProcessedGeometry {
  input: { pointCount: number; boundingBox: { minLat: number; minLng: number; maxLat: number; maxLng: number } };
  output: {
    pointCount: number;
    ring: { lat: number; lng: number }[];
    geojson: { type: 'Polygon'; coordinates: number[][][] };
    areaHectares: number;
    perimeterMeters: number;
    reductionPct: number;
  };
  stages: Array<{ name: string; inputCount: number; outputCount: number; durationMs: number }>;
  validation: {
    valid: boolean;
    issues: string[];
    metrics: {
      pointCount: number;
      perimeterMeters: number;
      areaHectares: number;
      longestEdgeMeters: number;
      selfIntersections: number;
    };
    cleanedRing: { lat: number; lng: number }[];
  };
  ok: boolean;
}

export interface SpatialEvent {
  id: string;
  event_type: string;
  entity_type: string;
  entity_id: string;
  farmer_id: string | null;
  payload: any;
  recorded_at: string;
}

export interface SpatialEventStats {
  total: number;
  last24h: number;
  byType: Record<string, number>;
  byEntityType: Record<string, number>;
}

export interface FarmVersion {
  id: string;
  farmerId: string;
  versionNumber: number;
  capturedAt: string;
  gpsLat: number | null;
  gpsLng: number | null;
  gpsPolygon: any;
  polygonAreaHectares: number | null;
  village: string | null;
  cooperative: string | null;
  triggerEvent: string | null;
  notes: string | null;
}

export interface DigitalTwinProfile {
  farmerId: string;
  currentLevel: number;
  hasBoundary: boolean;
  hasCrops: boolean;
  hasWeather: boolean;
  hasSatellite: boolean;
  hasIotSensors: boolean;
  hasAi: boolean;
  hasPredictive: boolean;
  levelHistory: Array<{ level: number; changedAt: string; reason: string }>;
  updatedAt: string;
}

export const spatialApi = {
  // ─── Geometry pipeline ───
  processGeometry: async (points: Array<{ lat: number; lng: number; accuracy?: number }>, options?: any): Promise<ProcessedGeometry> => {
    const res = await apiClient.post('/spatial/geometry/process', { points, options });
    return res.data;
  },

  // ─── Events ───
  getEvents: async (limit = 100, type?: string): Promise<SpatialEvent[]> => {
    const params: any = { limit };
    if (type) params.type = type;
    const res = await apiClient.get('/spatial/events', { params });
    return res.data.events;
  },

  getEventStats: async (): Promise<SpatialEventStats> => {
    const res = await apiClient.get('/spatial/events/stats');
    return res.data;
  },

  /** Open an SSE connection for live events. Returns a cleanup function. */
  streamEvents: (onEvent: (event: SpatialEvent) => void, onError?: (err: Event) => void): (() => void) => {
    const base = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
    const url = `${base}/spatial/events/stream`;
    // EventSource doesn't support custom headers — rely on cookie-based auth (withCredentials)
    // The authenticate middleware checks req.cookies.access_token
    const es = new EventSource(url, { withCredentials: true });
    const handler = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        onEvent(data);
      } catch {}
    };
    // Listen to all event types — EventSource fires `event.type` as the event name
    // but we registered without a specific type, so default `message` channel.
    es.addEventListener('message', handler);
    // Also listen for named events
    const types = ['GPS_POINT_RECEIVED', 'TRACK_UPDATED', 'POLYGON_CREATED', 'POLYGON_UPDATED', 'AREA_CHANGED', 'FIELD_CREATED', 'BOUNDARY_APPROVED', 'CROP_PLANTED', 'WEATHER_SNAPSHOT', 'SYNC_COMPLETED', 'TWIN_LEVEL_CHANGED'];
    for (const t of types) es.addEventListener(t, handler);
    if (onError) es.onerror = onError;
    return () => { es.close(); };
  },

  // ─── Temporal versioning ───
  getFarmHistory: async (farmerId: string, limit = 50): Promise<FarmVersion[]> => {
    const res = await apiClient.get(`/spatial/farms/${farmerId}/history`, { params: { limit } });
    return res.data.versions;
  },

  getFarmVersionAtDate: async (farmerId: string, date: string): Promise<FarmVersion | null> => {
    const res = await apiClient.get(`/spatial/farms/${farmerId}/version/${date}`);
    return res.data.version || null;
  },

  // ─── Feature store ───
  getFeatures: async (farmerId: string): Promise<Record<string, any>> => {
    const res = await apiClient.get(`/spatial/features/${farmerId}`);
    return res.data.features;
  },

  // ─── Digital twin ───
  getTwinProfile: async (farmerId: string): Promise<DigitalTwinProfile> => {
    const res = await apiClient.get(`/spatial/twin/${farmerId}`);
    return res.data;
  },

  recordCrop: async (farmerId: string, data: {
    cropName: string; variety?: string; areaHectares?: number;
    plantedAt?: string; expectedYieldKg?: number; notes?: string;
  }): Promise<any> => {
    const res = await apiClient.post(`/spatial/twin/${farmerId}/crop`, data);
    return res.data;
  },

  recordWeather: async (farmerId: string, data: {
    lat: number; lng: number; temperatureC?: number; humidityPct?: number;
    rainfallMm?: number; source?: string;
  }): Promise<any> => {
    const res = await apiClient.post(`/spatial/twin/${farmerId}/weather`, data);
    return res.data;
  },

  // ─── Health ───
  health: async (): Promise<any> => {
    const res = await apiClient.get('/spatial/health');
    return res.data;
  },
};

export default spatialApi;
